<template>
    <div>
        <div class="grid grid-cols-4 gap-4">
            <div class="col-span-4 flex items-center text-base justify-center rounded-md py-2"
                 :class="[form && form.resolved && ticket && ticket.resolved_at ? 'text-green-500 bg-green-50' : 'text-red-400 bg-red-50']">
                <label class="font-medium mr-3 leading-snug pt-0.5"> <span class="text-base font-medium">
                    {{ resolvedText }}</span>
                </label>
                <toggle v-model="form.resolved"
                        active-color-bg-class="bg-green-600"
                        active-color-text-class="text-green-600"
                        @input="toggleTicketResolveState">
                </toggle>
            </div>

            <div class="col-span-4 xl:col-span-2">
                <label class="font-normal">Priority</label>
                <simple-radio-group-blocks :labels="radioButtonOptions" group-name="priority" :background-colours="['green-100', 'indigo-100', 'red-100']"
                                           :text-colours="['green-600', 'indigo-600', 'red-600']"
                                           :default-selected-item-index="radioButtonOptions.indexOf(ticket.priority)" v-model="form.priority">
                </simple-radio-group-blocks>
            </div>

            <div class="col-span-4 xl:col-span-2" v-if="!ticket.archived_at && !(ticket.resolved_at && ticket.status === 'closed')">
                <label class="font-normal whitespace-nowrap">Next To-do</label>
                <div v-if="ticket.next_todo && Object.keys(ticket.next_todo).length > 0" class="flex justify-between">
                    <button class="flex font-medium hover:text-indigo-600 transition duration-300 space-x-1 items-center" @click="toggleCreateTodoModal()">
                        <icon-pencil-alt class="w-4 h-4"></icon-pencil-alt>
                        <span>
                            {{
                                ticket.next_todo && ticket.next_todo.latest_reminder ?
                                    ticket.next_todo.latest_reminder.reminder_date : "Set Reminder" | date
                            }}
                        </span>
                    </button>

                    <button @click="confirmCompleteTodo()">
                        <icon-thumbs-up class="w-4 h-4 hover:text-indigo-600 transition duration-300" title="Complete To-do"></icon-thumbs-up>
                    </button>
                </div>

                <button v-else class="flex font-medium hover:text-indigo-600 transition duration-300 space-x-1 items-center" @click="toggleCreateTodoModal()">
                    <icon-plus-circle class="w-4 h-4"></icon-plus-circle>
                    <span>Create To-do</span>
                </button>
            </div>
            <div v-else class="col-span-4 xl:col-span-2"><!--Empty div for spacing--></div>

            <div class="col-span-4 xl:col-span-2">
                <label class="font-normal whitespace-nowrap">Ticket Phase</label>
                <el-select v-model="form.phase_id" class="w-full" placeholder="Select Phase" filterable clearable>
                    <el-option
                        v-for="(phase, index) in phases"
                        :key="index"
                        :label="phase.phase"
                        :value="phase.id"
                    ></el-option>
                </el-select>
                <span class="invalid-feedback" :class="{'d-block': form.errors.has('phase_id')}" v-show="form.errors.has('phase_id')">
                    {{ form.errors.get("phase_id") }}
                </span>
            </div>

            <div class="col-span-4 xl:col-span-2">
                <label class="font-normal whitespace-nowrap">Assigned To</label>
                <el-select v-model="form.assigned_to" class="w-full" placeholder="Select user" clearable>
                    <el-option
                        @change="inputChanged"
                        v-for="user in users"
                        :key="user.id"
                        :label="user.name"
                        :value="user.id"
                    ></el-option>
                </el-select>
                <span class="invalid-feedback" :class="{'d-block': form.errors.has('user')}" v-show="form.errors.has('user')">
                    {{ form.errors.get("user") }}
                </span>
            </div>

            <div class="col-span-4 xl:col-span-2">
                <label class="font-normal whitespace-nowrap">Reported By</label>
                <el-select v-model="form.reporter" class="w-full" placeholder="Select type">
                    <el-option
                        v-for="reporter in reporterOptions"
                        :key="reporter"
                        :label="reporter"
                        :value="reporter"
                    ></el-option>
                </el-select>
                <span class="invalid-feedback" :class="{'d-block': form.errors.has('reporter')}" v-show="form.errors.has('reporter')">
                    {{ form.errors.get("reporter") }}
                </span>
            </div>

            <div class="col-span-4 xl:col-span-2">
                <label class="font-normal whitespace-nowrap">Report Method</label>
                <el-select v-model="form.report_method" class="w-full" placeholder="Select Report Method">
                    <el-option
                        v-for="reportMethod in reportMethodOptions"
                        :key="reportMethod"
                        :label="reportMethod"
                        :value="reportMethod"
                    ></el-option>
                </el-select>
                <span class="invalid-feedback" :class="{'d-block': form.errors.has('report_method')}" v-show="form.errors.has('report_method')">
                    {{ form.errors.get("report_method") }}
                </span>
            </div>

            <!-- Opened from if opened from maintenance request or inspection-->
            <div class="col-span-4" v-if="ticket.maintenance_requests && ticket.maintenance_requests.length > 0 || ticket.inspections && ticket.inspections.length > 0">
                <label class="font-normal whitespace-nowrap">Opened From</label>
                <div class="flex flex-col gap-4">
                    <div v-if="ticket.maintenance_requests && ticket.maintenance_requests.length > 0" class="flex flex-wrap items-center gap-2">
                        <a :href="'/maintenance/requests?filter_text=' + maintenanceRequest.reference_number + '&status=' + maintenanceRequest.status" target="_blank"
                           v-for="maintenanceRequest in ticket.maintenance_requests" :key="maintenanceRequest.id">
                            <badge class="transition duration-300"
                                   :class="maintenanceRequest.status === 'new' ? 'bg-indigo-100 text-indigo-500 hover:bg-indigo-200 hover:text-indigo-600' :
                                'bg-green-100 text-green-500 hover:bg-green-200 hover:text-green-600'">
                                {{ maintenanceRequest.reference_number }}
                            </badge>
                        </a>
                    </div>

                    <div v-if="ticket.inspections && ticket.inspections.length > 0" class="flex flex-wrap items-center gap-2">
                        <a :href="'/inspections/' + inspection.id" target="_blank"
                           v-for="inspection in ticket.inspections" :key="inspection.id">
                            <badge class="transition duration-300"
                                   :class="inspection.status === 'scheduled' ? 'bg-indigo-100 text-indigo-500 hover:bg-indigo-200 hover:text-indigo-600' :
                       (inspection.status === 'completed' ?'bg-green-100 text-green-500 hover:bg-green-200 hover:text-green-600' : 'bg-yellow-100 text-yellow-500 hover:bg-yellow-200 hover:text-yellow-600')">
                                {{ inspection.type }} On {{ inspection.date | localeUnawareDatetime }}
                            </badge>
                        </a>
                    </div>
                </div>
            </div>

            <div class="col-span-4">
                <label class="font-normal whitespace-nowrap">Description</label>
                <textarea
                    v-model="form.description"
                    class="form-control form-control-line" :class="{'is-invalid': form.errors.has('description')}"
                    name="description"
                    rows="6" style="min-height: 3rem">
               </textarea>
                <span class="invalid-feedback" :class="{'d-block': form.errors.has('description')}" v-show="form.errors.has('description')">
                    {{ form.errors.get("description") }}
                </span>
            </div>

            <transition-group name="slide" class="col-span-4 grid grid-cols-4 gap-4">
                <template v-if="showInsuranceDetails">
                    <div class="col-span-4 md:col-span-2" key="0">
                        <label class="font-normal">Contracted Value</label>
                        <div class="input-group form-group">
                            <div class="input-group-prepend">
                                <span class="input-group-text">ZAR</span>
                            </div>
                            <input
                                type="text"
                                class="form-control"
                                name="contracted_value"
                                @keypress="onlyNumber"
                                @blur="roundContractedValue"
                                v-model="form.contracted_value"
                                :class="{'is-invalid': form.errors.has('contracted_value')}"
                            />
                        </div>
                        <span class="invalid-feedback" v-show="form.errors.has('contracted_value')">
                            {{ form.errors.get("contracted_value") }}
                        </span>
                    </div>

                    <div class="col-span-4 md:col-span-2" key="1">
                        <label class="font-normal">Payment Responsibility</label>
                        <el-select v-model="form.payment_responsibility" class="w-full" :class="{'invalid-feedback': form.errors.has('payment_responsibility')}">
                            <el-option value="Agency"></el-option>
                            <el-option value="Landlord"></el-option>
                            <el-option value="Tenant"></el-option>
                            <el-option value="Insurer"></el-option>
                            <el-option value="Service Provider"></el-option>
                            <el-option value="No Charge"></el-option>
                        </el-select>
                        <span v-show="form.errors.has('payment_responsibility')" class="invalid-feedback"
                              :class="{'d-block': form.errors.has('payment_responsibility')}">
                            {{ form.errors.get("payment_responsibility") }}
                        </span>
                    </div>

                    <div class="col-span-4 md:col-span-2" key="2">
                        <label class="font-normal">Insurer</label>
                        <input
                            v-model="form.insurer"
                            id="insurer"
                            type="text"
                            name="insurer"
                            class="form-control"
                            :class="{'is-invalid': form.errors.has('insurer')}"
                        />
                        <span class="invalid-feedback" v-show="form.errors.has('insurer')">
                            {{ form.errors.get("insurer") }}
                        </span>
                    </div>

                    <div class="col-span-4 md:col-span-2" key="3">
                        <label class="font-normal">Claim Number</label>
                        <input
                            v-model="form.insurance_claim_number"
                            type="text"
                            name="insurance_claim_number"
                            class="form-control"
                            :class="{'is-invalid': form.errors.has('insurance_claim_number')}"
                        />
                        <span v-show="form.errors.has('insurance_claim_number')"
                              class="invalid-feedback">
                            {{ form.errors.get("insurance_claim_number") }}
                        </span>
                    </div>

                    <div class="col-span-4" key="4">
                        <label class="font-normal">Insurance Notes</label>
                        <textarea
                            v-model="form.insurance_notes"
                            type="text"
                            rows="3" style="min-height: 3rem"
                            class="form-control form-control-line" :class="{'is-invalid': form.errors.has('insurance_notes')}">
                </textarea>
                        <span v-show="form.errors.has('insurance_notes')" class="invalid-feedback">
                            {{ form.errors.get("insurance_notes") }}
                        </span>
                    </div>
                </template>
            </transition-group>
        </div>

        <!-- Insurance Detail -->
        <button class="flex gap-2 items-center w-full text-sm text-gray-400 bg-white hover:text-indigo-600 transition duration-300 mb-4"
                :class="{'mt-2' : showInsuranceDetails}"
                @click="toggleInsuranceDetails()">
            <span>{{ showInsuranceDetails ? "Hide " : "Show " }} Contracted Values & Insurance Details</span>
            <icon-chevron-down class="h-4 w-4 transform transition duration-300" :class="showInsuranceDetails ? 'rotate-180' : ''"></icon-chevron-down>
        </button>

        <div class="mt-1 w-full flex justify-end">
            <secondary-button @click.prevent="updateTicketDetails" :disabled="form.busy">
                Save
            </secondary-button>
        </div>

        <simple-modal :show="showCreateTodoModal" @close="toggleCreateTodoModal()"
                      :title="ticket.next_todo ? 'Update To-do' : 'Create To-do'">
            <todo-create-edit-form :ticket="ticket" :todo="ticket.next_todo" @todo-updated="todoUpdated" @todo-created="todoUpdated"
                                   @close="toggleCreateTodoModal"></todo-create-edit-form>
        </simple-modal>

        <confirmation-dialog
            :show="showConfirmCompleteDialog"
            title="Complete To-do"
            :subtitle="'This to-do has a repeat option enabled. Another to-do will be created for ' + todoRepeatOnDate"
            :show-icon="false"
            cancel-button-text="Don't Repeat"
            proceed-button-text="Repeat"
            proceed-button-colour="indigo-600"
            proceed-button-hover-colour="indigo-400"
            @cancelled="completeTodo(false)"
            @proceed="completeTodo()"
        ></confirmation-dialog>
    </div>
</template>

<script>
export default {
    name: "TicketMainDetails",

    props: [
        "users",
        "ticket",
        "reporterOptions",
        "reportMethodOptions",
        "phases"
    ],

    data () {
        return {
            form: new AppForm({}),
            showInsuranceDetails: false,
            radioButtonOptions: ["Low", "Medium", "High"],
            showCreateTodoModal: false,
            showConfirmCompleteDialog: false,
        };
    },

    watch: {
        ticket: function () {
            this.initForm();
        },

        form: function (newVal) {
            this.form = newVal;
        },

        "ticket.description": function () {
            this.form.description = this.ticket.description;
        },

        "ticket.resolved_at": function (newVal) {
            this.form.resolved = !!newVal;
        },
    },

    computed: {
        resolvedText () {
            return this.form && this.form.resolved && this.ticket && this.ticket.resolved_at ?
                "Resolved on " + this.$dayjs(this.ticket.resolved_at).format("D MMM YYYY") :
                "Unresolved";
        },

        todoRepeatOnDate () {
            if (!this.ticket.next_todo || (this.ticket.next_todo && this.ticket.next_todo.latest_reminder && this.ticket.next_todo.latest_reminder.repeat === "never")) {
                return null;
            }
            let reminder = this.ticket.next_todo.latest_reminder;
            if (reminder && reminder.repeat === "periodically") {
                let intervalType = reminder.interval_type;
                if (intervalType.endsWith("s")) {
                    intervalType = intervalType.substr(0, intervalType.length - 1);
                }
                return this.$dayjs(reminder.reminder_date).add(reminder.interval, intervalType).format("D MMM YYYY");
            } else if (reminder && reminder.repeat === "once") {
                return this.$dayjs(reminder.repeat_once_on).format("D MMM YYYY");
            }
        }
    },

    mounted () {
        this.initForm();

        let self = this;
        Bus.$on("service-provider-tasked", function () {
            self.initForm();
        });
    },

    methods: {
        initForm () {
            this.form = new AppForm({
                assigned_to: this.users.find(u => u.id === this.ticket.assigned_to)?.id,
                priority: this.ticket.priority,
                reporter: this.ticket.reporter,
                report_method: this.ticket.report_method,
                description: this.ticket.description,
                phase_id: this.ticket.phase_id,
                contracted_value: this.ticket.contracted_value,
                payment_responsibility: this.ticket.payment_responsibility,
                insurer: this.ticket.insurer,
                insurance_claim_number: this.ticket.insurance_claim_number,
                insurance_notes: this.ticket.insurance_notes,
                resolved: !!this.ticket.resolved_at
            });
        },

        inputChanged () {
            this.form.errors.forget();
        },

        toggleTicketResolveState () {
            if (this.form.resolved !== !!this.ticket.resolved_at) {
                axios.patch(`${this.ticket.id}/properties/resolve/change`)
                .then((response) => {
                    this.$emit("resolve-date-changed", response.data.resolved_at);
                    if (response.data.resolved_at && this.ticket.status === "closed") {
                        this.$emit("todo-changed", null);
                    }
                    Bus.$emit("refresh-activity-list");
                })
                .catch(() => {
                    toastr.error("Could not update ticket resolution status!");
                });
            }
        },

        updateTicketDetails () {
            this.form.errors.forget();

            App.patch("/tickets/" + this.ticket.id + "/update", this.form)
            .then(response => {
                Bus.$emit("refresh-activity-list");
                Bus.$emit("ticket-updated", response);
                toastr.success("Ticket Details Updated!");
            }).catch((error) => {
                toastr.error("Could Not Update Ticket! Error: " + error.message);
            });
        },

        toggleInsuranceDetails () {
            this.showInsuranceDetails = !this.showInsuranceDetails;
        },

        roundContractedValue () {
            this.form.contracted_value = Number(this.form.contracted_value).toFixed(2);
        },

        onlyNumber (event) {
            let keyCode = (event.keyCode ? event.keyCode : event.which);
            if ((keyCode < 48 || keyCode > 57) && keyCode !== 46) { // 46 is dot
                event.preventDefault();
            }
        },

        toggleCreateTodoModal () {
            this.showCreateTodoModal = !this.showCreateTodoModal;
        },

        todoUpdated (updatedTodo) {
            this.$emit("todo-changed", updatedTodo);
        },

        confirmCompleteTodo () {
            if (!this.ticket.next_todo.processed_at && this.ticket.next_todo.latest_reminder && this.ticket.next_todo.latest_reminder.repeat !== "never") {
                this.showConfirmCompleteDialog = true;
                return;
            }
            this.completeTodo();
        },

        completeTodo (shouldRepeat = true) {
            this.showConfirmCompleteDialog = false;
            App.patch(`/tickets/${this.ticket.id}/todos/${this.ticket.next_todo.id}/complete`, new AppForm({
                should_repeat: shouldRepeat
            }))
            .then(response => {
                this.$emit("todo-changed", response);
                toastr.success("Successfully Completed To-do");
            })
            .catch(error => {
                toastr.error("Could not complete to-do. Error: " + error.message);
            });
        },
    },
};
</script>
